import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * The console's crash fence (pre-pilot QA #22).
 *
 * Without one, a single render throw — one malformed row, one status value a
 * lookup map has never seen — unmounted the WHOLE app to a white page for
 * every agent at once, with the session intact and no way back but a manual
 * reload. The boundary wraps the routed screen, so the shell and the nav
 * survive and the person moves to another screen or retries this one.
 *
 * `key`ed by route in App.tsx: navigating away resets the boundary, so one
 * broken screen never traps the user on the error card.
 */
interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // The console has no error pipeline yet; the browser console is where a
    // developer will look first, with the component stack attached.
    console.error('screen crashed:', error, info.componentStack);
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="mx-auto max-w-lg px-5 py-16 text-center">
          <p className="text-sm font-semibold text-ink-900">This screen hit an error.</p>
          <p className="mt-2 text-xs text-ink-500">
            The rest of the console still works — use the navigation, or retry this screen. If it
            keeps happening, tell whoever runs the deploy and mention what you clicked; the
            details are in the browser console.
          </p>
          <button
            type="button"
            onClick={() => this.setState({ error: null })}
            className="mt-4 rounded-lg bg-ink-900 px-4 py-2 text-sm font-semibold text-paper"
          >
            Retry
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
